/**
 * F34 — Sheet di personalizzazione dashboard.
 *
 * Permette di:
 *  - riordinare le card hero con drag&drop
 *  - nascondere/mostrare singole card hero
 *  - nascondere/mostrare tab
 *  - reset al layout di default
 */
import React, { useState, useEffect } from 'react';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  DndContext, DragEndEvent, PointerSensor, useSensor, useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, RotateCcw, Eye, EyeOff } from 'lucide-react';

import type {
  DashboardLayout, HeroCardId, DashboardTabId,
} from '@/hooks/useDashboardPreferences';
import { DEFAULT_LAYOUT } from '@/hooks/useDashboardPreferences';

/** Label per le card hero (in italiano). */
export const HERO_CARD_LABELS: Record<HeroCardId, string> = {
  won: 'Vinti nel periodo',
  pipeline: 'Pipeline aperta',
  margin: 'Margine medio % (gestionale)',
  conversion: 'Conversion rate',
  avg_close: 'Tempo medio chiusura',
  avg_value: 'Valore medio offerta',
  lost: 'Persi nel periodo',
};

export const TAB_LABELS: Record<DashboardTabId, string> = {
  overview: 'Panoramica',
  sales: 'Commerciale',
  materials: 'Materiali & Fornitori',
  operational: 'Operativo',
};

interface DashboardCustomizeSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  layout: DashboardLayout;
  onSave: (layout: DashboardLayout) => Promise<DashboardLayout> | void;
  onReset: () => Promise<void> | void;
  isSaving?: boolean;
}

interface SortableHeroRowProps {
  cardId: HeroCardId;
  hidden: boolean;
  onToggleVisibility: (cardId: HeroCardId) => void;
}

const SortableHeroRow: React.FC<SortableHeroRowProps> = ({ cardId, hidden, onToggleVisibility }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cardId });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2 rounded border bg-card hover:bg-muted/30 transition-colors"
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded touch-none"
        {...attributes}
        {...listeners}
        aria-label={`Sposta ${HERO_CARD_LABELS[cardId]}`}
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </button>
      <span className={`flex-1 text-sm font-medium ${hidden ? 'text-muted-foreground line-through' : ''}`}>
        {HERO_CARD_LABELS[cardId]}
      </span>
      <button
        type="button"
        onClick={() => onToggleVisibility(cardId)}
        className="p-1.5 hover:bg-muted rounded transition-colors"
        aria-label={hidden ? `Mostra ${HERO_CARD_LABELS[cardId]}` : `Nascondi ${HERO_CARD_LABELS[cardId]}`}
      >
        {hidden ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-emerald-600" />}
      </button>
    </div>
  );
};

const DashboardCustomizeSheet: React.FC<DashboardCustomizeSheetProps> = ({
  open, onOpenChange, layout, onSave, onReset, isSaving,
}) => {
  // Stato locale "draft": modifiche fatte ma non ancora salvate
  const [draft, setDraft] = useState<DashboardLayout>(layout);

  // Quando si riapre lo sheet o cambia il layout salvato, allinea il draft
  useEffect(() => {
    if (open) setDraft(layout);
  }, [open, layout]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = draft.hero_cards_order.indexOf(active.id as HeroCardId);
    const newIndex = draft.hero_cards_order.indexOf(over.id as HeroCardId);
    if (oldIndex < 0 || newIndex < 0) return;
    setDraft(d => ({ ...d, hero_cards_order: arrayMove(d.hero_cards_order, oldIndex, newIndex) }));
  };

  const toggleHeroHidden = (cardId: HeroCardId) => {
    setDraft(d => ({
      ...d,
      hero_cards_hidden: d.hero_cards_hidden.includes(cardId)
        ? d.hero_cards_hidden.filter(c => c !== cardId)
        : [...d.hero_cards_hidden, cardId],
    }));
  };

  const toggleTabHidden = (tabId: DashboardTabId, visible: boolean) => {
    setDraft(d => ({
      ...d,
      tabs_hidden: visible
        ? d.tabs_hidden.filter(t => t !== tabId)
        : [...d.tabs_hidden, tabId],
    }));
  };

  const handleSave = async () => {
    try {
      await onSave(draft);
      toast.success('Layout dashboard salvato');
      onOpenChange(false);
    } catch (e) {
      console.error('[F34] save layout failed:', e);
      toast.error('Errore nel salvataggio del layout');
    }
  };

  const handleReset = async () => {
    try {
      await onReset();
      setDraft(DEFAULT_LAYOUT);
      toast.success('Layout ripristinato al default');
    } catch (e) {
      console.error('[F34] reset layout failed:', e);
      toast.error('Errore nel ripristino del layout');
    }
  };

  // Tutti i tab disponibili (per costruire la sezione visibilità)
  const allTabs: DashboardTabId[] = ['overview', 'sales', 'materials', 'operational'];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Personalizza dashboard</SheetTitle>
          <SheetDescription>
            Riordina le card, nascondi quelle che non ti servono, scegli quali tab vedere.
            Le preferenze sono solo tue (non vengono condivise con il team).
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* CARD HERO */}
          <section>
            <h3 className="text-sm font-semibold mb-1">Card principali</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Trascina per riordinare. Clicca l'occhio per nascondere.
            </p>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={draft.hero_cards_order} strategy={verticalListSortingStrategy}>
                <div className="space-y-1.5">
                  {draft.hero_cards_order.map(cardId => (
                    <SortableHeroRow
                      key={cardId}
                      cardId={cardId}
                      hidden={draft.hero_cards_hidden.includes(cardId)}
                      onToggleVisibility={toggleHeroHidden}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </section>

          <Separator />

          {/* TAB */}
          <section>
            <h3 className="text-sm font-semibold mb-1">Tab visibili</h3>
            <p className="text-xs text-muted-foreground mb-3">
              I tab nascosti non appariranno nella dashboard.
            </p>
            <div className="space-y-2">
              {allTabs.map(tabId => {
                const visible = !draft.tabs_hidden.includes(tabId);
                return (
                  <div key={tabId} className="flex items-center justify-between p-2 rounded border">
                    <span className={`text-sm font-medium ${visible ? '' : 'text-muted-foreground'}`}>
                      {TAB_LABELS[tabId]}
                    </span>
                    <Switch
                      checked={visible}
                      onCheckedChange={(checked) => toggleTabHidden(tabId, checked)}
                      aria-label={`Tab ${TAB_LABELS[tabId]}`}
                    />
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        <SheetFooter className="mt-6 flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button onClick={handleSave} disabled={isSaving} className="w-full">
            {isSaving ? 'Salvataggio…' : 'Salva preferenze'}
          </Button>
          <Button variant="outline" onClick={handleReset} disabled={isSaving} className="w-full gap-2">
            <RotateCcw className="h-4 w-4" />
            Ripristina default
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default DashboardCustomizeSheet;
